import {
  Button,
  InlineNotification,
  Modal,
  Tag,
  TextArea,
} from '@carbon/react';
import { Play } from '@carbon/react/icons';
import { useSetAtom } from 'jotai';
import { useState } from 'react';
import { createPortal } from 'react-dom';

import { parseLocalDateTime } from 'utils/dates';

import { addHoldCommentAtom, bulkReleaseAtom, holdTractAtom } from '../atoms';
import type { SlotInterestRow, SlotSubtract, StreamCode } from '../types';
import { getMutationErrorMessage, STREAM_LABELS } from '../utils';
import { useWorkspaceConfirmation } from '../workspace-confirmation-context';
import styles from './tract-hold-section.module.scss';

/**
 * Formats an ISO timestamp for the hold header / comment thread; em-dash for
 * null. Uses the shared `parseLocalDateTime` so a hold event renders the same
 * local time here as in the Activity Feed (no timezone skew between surfaces).
 *
 * @param {string | null} iso - The ISO timestamp.
 * @return {string} The formatted timestamp.
 */
const formatTimestamp = (iso: string | null): string => {
  if (iso === null) return '—';
  return parseLocalDateTime(iso, true);
};

interface TractHoldSectionProps {
  row: SlotInterestRow;
  /** The tract (real subtract) this hold targets. Holds are per-subtract, so
   *  the no-subtract pseudo-tract never renders this section. */
  subtract: SlotSubtract;
  /** Whether the card's "On Hold" button has opened the put-on-hold modal. */
  open: boolean;
  /** Closes the put-on-hold modal (Cancel / X / successful hold). */
  onClose: () => void;
  /** While the parent card's editor is open on a held tract, the live stream
   *  selection to preview in the held-streams tags so the red tags track the
   *  Streams checkboxes as the user toggles them. `null` when the editor is
   *  closed — the tags then show the persisted `hold.heldStreams`. */
  previewStreams?: StreamCode[] | null;
  /** The streams currently checked in the editor — what "Put on hold" actually
   *  holds. Lets the user hold a subset (e.g. only Gas) instead of the whole
   *  tract. */
  selectedStreams: StreamCode[];
  /** Panel-level lock: a submit is in flight somewhere in the panel, or the
   *  active row is stale (INS-11586). These actions mutate the same slot the
   *  panel is showing, so they have to honour it too — otherwise the "Editing
   *  is locked" banner is telling the truth about Certify and lying about
   *  Clear Review, Add Comment and Mark for Review. Distinct from the local
   *  `submitting` state, which only covers THIS section's own in-flight write. */
  locked?: boolean;
}

/**
 * Per-tract On Hold overlay inside the Confirm Interest panel (INS-9896). The
 * "put on hold" flow is a centered modal (reason only — all of the slot's
 * streams are held); when the tract is already held, the held block renders
 * inline below the card with who/when, the held streams, the comment thread,
 * an append-comment box, and Release. All three actions go through the per-tract
 * hold atoms, which refetch the slot list on success.
 *
 * @param {TractHoldSectionProps} props - Component props.
 * @return {JSX.Element} The held block and/or the put-on-hold modal.
 */
export const TractHoldSection = ({
  row,
  subtract,
  open,
  onClose,
  previewStreams = null,
  selectedStreams,
  locked = false,
}: TractHoldSectionProps): JSX.Element => {
  const { workspaceId, workspace } = useWorkspaceConfirmation();
  const holdTract = useSetAtom(holdTractAtom);
  const bulkRelease = useSetAtom(bulkReleaseAtom);
  const addComment = useSetAtom(addHoldCommentAtom);

  // `hold` is optional on the wire-absent path (fixtures / confirm response);
  // normalize undefined → null so the held/not-held branch is unambiguous.
  const hold = subtract.hold ?? null;
  const [reason, setReason] = useState('');
  const [commentBody, setCommentBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The panel is read-only on a promoted workspace (BE rejects the writes too).
  // `locked` folds in the panel's own submit-in-flight / stale-row lock.
  const disabled = submitting || locked || workspace.promoted;
  const tractName =
    subtract.tractName ?? subtract.subtractName ?? subtract.subtractId;
  // Per-UAT grain (ONCALL-668): name the UAT so two UAT segments of the same
  // subtract are distinguishable in the hold modal / held block.
  const uatLabel = `UAT: ${subtract.uatName ?? '—'}`;

  const handleHold = async (): Promise<void> => {
    if (reason.trim() === '') return;
    setSubmitting(true);
    setError(null);
    try {
      await holdTract(workspaceId, row.workspaceSlotId, {
        hold: {
          subtract_id: subtract.subtractId,
          // Per-UAT grain (ONCALL-668): hold the right (subtract, UAT) segment.
          unit_allocation_tract_id: subtract.uatId ?? null,
          comment: reason.trim(),
          // Hold only the streams checked in the editor (the BE places one
          // SlotHold per stream, so a single-stream selection holds just that).
          // Falls back to the whole tract when nothing is selected.
          streams: selectedStreams.length > 0 ? selectedStreams : row.streams,
        },
      });
      setReason('');
      onClose();
    } catch (caught) {
      setError(
        getMutationErrorMessage(caught, "Couldn't mark tract for review.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRelease = async (): Promise<void> => {
    const heldStreams = hold?.heldStreams ?? [];
    if (heldStreams.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      // Release every held stream of the tract in one bulk request: the BE
      // stamps a shared `released_at` so the feed shows one "Removed On Hold"
      // card, AND all held streams clear — the single-stream DELETE only
      // cleared the representative workspace_slot, leaving siblings held.
      await bulkRelease(workspaceId, [
        {
          slot_id: row.slotId,
          subtract_id: subtract.subtractId,
          // Per-UAT grain (ONCALL-668): release the right (subtract, UAT)
          // segment so a sibling UAT's hold on the same tract stays held.
          unit_allocation_tract_id: subtract.uatId ?? null,
          streams: heldStreams,
        },
      ]);
    } catch (caught) {
      setError(getMutationErrorMessage(caught, "Couldn't clear review."));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddComment = async (): Promise<void> => {
    if (commentBody.trim() === '') return;
    setSubmitting(true);
    setError(null);
    try {
      await addComment(workspaceId, row.workspaceSlotId, {
        hold: {
          subtract_id: subtract.subtractId,
          unit_allocation_tract_id: subtract.uatId ?? null,
          body: commentBody.trim(),
        },
      });
      setCommentBody('');
    } catch (caught) {
      setError(getMutationErrorMessage(caught, "Couldn't add comment."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {hold !== null && (
        <div className={styles.heldBlock}>
          {error !== null && (
            <InlineNotification
              className={styles.notification}
              kind="error"
              lowContrast
              hideCloseButton
              title={error}
            />
          )}
          <div className={styles.heldHeader}>
            <Tag type="red" size="sm">
              Needs Further Review
            </Tag>
            <span className={styles.heldMeta}>
              {hold.heldByName ?? 'Unknown'} · {formatTimestamp(hold.heldAt)} ·{' '}
              {uatLabel}
            </span>
          </div>
          {/* While the card's editor is open, mirror the live Streams
              selection (previewStreams) so the red tags always reflect what's
              currently checked; otherwise show the persisted held streams. */}
          <div className={styles.heldStreams}>
            {(previewStreams ?? hold.heldStreams).map((stream) => (
              <Tag key={stream} type="red" size="sm">
                {STREAM_LABELS[stream]}
              </Tag>
            ))}
          </div>
          <ul className={styles.thread}>
            {hold.comments.map((comment) => (
              <li key={comment.id} className={styles.comment}>
                <span className={styles.commentMeta}>
                  {comment.userName} · {formatTimestamp(comment.createdAt)}
                </span>
                <span className={styles.commentBody}>{comment.body}</span>
              </li>
            ))}
          </ul>
          <TextArea
            id={`hold-comment-${subtract.subtractId}`}
            labelText="Add a comment"
            rows={2}
            placeholder="Append to the review thread…"
            value={commentBody}
            onChange={(event) => setCommentBody(event.target.value)}
          />
          <div className={styles.actions}>
            <Button
              kind="danger--tertiary"
              size="sm"
              renderIcon={Play}
              disabled={disabled}
              onClick={handleRelease}
            >
              Clear Review
            </Button>
            <Button
              kind="secondary"
              size="sm"
              disabled={disabled || commentBody.trim() === ''}
              onClick={handleAddComment}
            >
              Add Comment
            </Button>
          </div>
        </div>
      )}

      {createPortal(
        <Modal
          open={open && hold === null}
          modalHeading={`Mark ${tractName} (${uatLabel}) for review`}
          primaryButtonText="Mark for Review"
          secondaryButtonText="Cancel"
          primaryButtonDisabled={disabled || reason.trim() === ''}
          onRequestClose={onClose}
          onRequestSubmit={handleHold}
        >
          <p className={styles.modalBody}>
            Marking this tract sends it for further review by other workspace
            users. Explain why it needs another look.
          </p>
          {error !== null && (
            <InlineNotification
              className={styles.notification}
              kind="error"
              lowContrast
              hideCloseButton
              title={error}
            />
          )}
          <TextArea
            id={`hold-reason-${subtract.subtractId}`}
            labelText="Reason for review (required)"
            rows={4}
            placeholder="e.g. Custom interest deviates significantly from Division Order — needs operator outreach."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Modal>,
        document.body
      )}
    </>
  );
};
